import _ from 'lodash';
import { uuid } from 'vue-uuid';

function getRotatedRectCorners(x, y, width, height, rotateDeg, origin = 'center') {
  const rad = (rotateDeg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);

  if (origin === 'topLeft') {
    const corners = [
      { x: 0, y: 0 },
      { x: width, y: 0 },
      { x: width, y: height },
      { x: 0, y: height },
    ];
    return corners.map(({ x: px, y: py }) => ({
      x: x + px * cos - py * sin,
      y: y + px * sin + py * cos,
    }));
  }

  const cx      = x + width / 2;
  const cy      = y + height / 2;
  const corners = [
    { x, y },
    { x: x + width, y },
    { x: x + width, y: y + height },
    { x, y: y + height },
  ];

  return corners.map(({ x: px, y: py }) => {
    const dx = px - cx;
    const dy = py - cy;
    return {
      x: cx + dx * cos - dy * sin,
      y: cy + dx * sin + dy * cos,
    };
  });
}

function getObjectDimensions(obj) {
  let x      = obj.X || 0;
  let y      = obj.Y || 0;
  let width  = (obj.Width || 0) * (obj.scaleX || 1);
  let height = (obj.Height || 0) * (obj.scaleY || 1);

  if (obj.type === 'shape') {
    if (obj.shapeType === 'ellipses') {
      if (!width || !height) {
        const radiusW = (obj.CurveHorizontalRadius || 0) * 2 * (obj.scaleX || 1);
        const radiusH = (obj.CurveVerticalRadius || 0) * 2 * (obj.scaleY || 1);
        width  = radiusW || width;
        height = radiusH || height;
      }
    } else if (obj.shapeType === 'polygon') {
      width  = (obj.polygonWidth || obj.Width || 0) * (obj.scaleX || 1);
      height = (obj.polygonHeight || obj.Height || 0) * (obj.scaleY || 1);
    }
  }

  return {
    x, y, width, height,
  };
}

export function updateEllipse(obj) {
  if (!obj || obj.type !== 'shape' || obj.shapeType !== 'ellipses') {
    return obj;
  }
  const width  = Number(obj.Width) || 0;
  const height = Number(obj.Height) || 0;
  if (width <= 0 || height <= 0) {
    return obj;
  }
  obj.CurveHorizontalRadius = width / 2;
  obj.CurveVerticalRadius   = height / 2;
  obj.CurveCenterX          = width / 2;
  obj.CurveCenterY          = height / 2;
  obj.svgScaleX             = 1;
  obj.svgScaleY             = 1;
  obj.isNewObject           = false;
  return obj;
}

export function getObjectBounds(obj, options = {}) {
  const {
    x, y, width, height,
  } = getObjectDimensions(obj);
  const rotate = obj.RotateDegree || 0;
  const origin = options.origin
      || (options.asGroupChild ? 'topLeft' : null)
      || (obj.type === 'text' || obj.type === 'image' || obj.type === 'table' ? 'topLeft' : 'center');

  if (rotate) {
    const corners = getRotatedRectCorners(x, y, width, height, rotate, origin);
    const xs      = corners.map(corner => corner.x);
    const ys      = corners.map(corner => corner.y);
    const minX    = Math.min(...xs);
    const minY    = Math.min(...ys);
    const maxX    = Math.max(...xs);
    const maxY    = Math.max(...ys);

    return {
      x:      minX,
      y:      minY,
      width:  maxX - minX,
      height: maxY - minY,
      right:  maxX,
      bottom: maxY,
    };
  }

  return {
    x,
    y,
    width,
    height,
    right:  x + width,
    bottom: y + height,
  };
}

export function computeBoundingBox(objects, options = {}) {
  const bounds = objects.map(obj => getObjectBounds(obj, options));
  const x      = Math.min(...bounds.map(b => b.x));
  const y      = Math.min(...bounds.map(b => b.y));
  const right  = Math.max(...bounds.map(b => b.right));
  const bottom = Math.max(...bounds.map(b => b.bottom));

  return {
    x,
    y,
    width:  right - x,
    height: bottom - y,
  };
}

export function flattenGroup(group) {
  if (!group || group.type !== 'group' || !Array.isArray(group.children)) {
    return [];
  }
  return group.children.map(child => ({
    ..._.cloneDeep(child),
    isSelected: false,
  }));
}

export function expandSelectionForProperties(targets) {
  if (!targets || !targets.length) { return []; }
  return targets.reduce((acc, target) => {
    if (!target) { return acc; }
    if (target.type === 'group') {
      return acc.concat(expandSelectionForProperties(target.children || []));
    }
    return acc.concat(target);
  }, []);
}

export function reassignGroupIds(group) {
  const clone = _.cloneDeep(group);
  clone.Id    = uuid.v1();

  if (clone.type === 'group' && Array.isArray(clone.children)) {
    clone.children = clone.children.map((child) => {
      const oldId    = child.Id;
      const newChild = _.cloneDeep(child);
      newChild.Id    = uuid.v1();
      newChild.isSelected = false;

      if (newChild.type === 'image'
          && newChild.SvgProperty
          && newChild.SvgProperty.content) {
        newChild.SvgProperty.content = newChild.SvgProperty.content.replaceAll(oldId, newChild.Id);
      }

      return newChild;
    });
  }

  return clone;
}

export function canGroupObjects(objects) {
  if (!objects || objects.length < 2) { return false; }
  return objects.every(obj => !obj.locked && obj.type !== 'group');
}

export function canAddLayersToGroup(objects) {
  if (!objects || !objects.length) { return false; }
  return objects.every(obj => !obj.locked && obj.type !== 'group');
}

export function replaceLayersWithGroup(list, layersToGroup, group, anchorId = null) {
  if (!list || !layersToGroup || !layersToGroup.length || !group) { return -1; }

  const selectedIds = new Set(layersToGroup.map(item => item.Id));
  let pivotIndex    = -1;

  if (anchorId && selectedIds.has(anchorId)) {
    pivotIndex = list.findIndex(item => item.Id === anchorId);
  }
  if (pivotIndex < 0) {
    pivotIndex = list.findIndex(item => selectedIds.has(item.Id));
  }
  if (pivotIndex < 0) {
    pivotIndex = 0;
  }

  const insertIndex = list
      .slice(0, pivotIndex)
      .filter(item => !selectedIds.has(item.Id)).length;

  for (let i = list.length - 1; i >= 0; i -= 1) {
    if (selectedIds.has(list[i].Id)) {
      list.splice(i, 1);
    }
  }

  list.splice(insertIndex, 0, group);
  return insertIndex;
}

export function addLayersToExistingGroup(group, layers) {
  if (!group || group.type !== 'group' || !layers.length) { return group; }

  const updatedGroup = _.cloneDeep(group);

  const nextChildren = [
    ...(updatedGroup.children || []),
    ...layers.map((layer) => {
      const child = _.cloneDeep(layer);
      child.isSelected = false;
      child.elementIndex = -1;
      return child;
    }),
  ];

  updatedGroup.children = nextChildren;
  updatedGroup.childCoords = 'absolute';
  updatedGroup.isGroupExpanded = true;
  recalculateGroupBounds(updatedGroup);
  assignPdfSortOrderWithinGroup(updatedGroup.children);

  return updatedGroup;
}
export function recalculateGroupBounds(group) {
  if (!group || !group.children || !group.children.length) {
    return group;
  }

  const bbox = computeBoundingBox(group.children, { asGroupChild: true });
  group.X      = bbox.x;
  group.Y      = bbox.y;
  group.Width  = bbox.width;
  group.Height = bbox.height;
  group.scaleX = 1;
  group.scaleY = 1;
  group.RotateDegree = 0;
  group.childCoords = 'absolute';

  return group;
}

export function getUniqueGroupName(objectList, baseName = 'Group') {
  let name = baseName;
  let i    = 1;
  while (objectList.find(item => item.Name === name)) {
    name = `${baseName} ${i}`;
    i   += 1;
  }
  return name;
}
export function findObjectById(objectList, id) {
  for (const obj of objectList) {
    if (obj.Id === id) { return obj; }
    if (obj.type === 'group' && obj.children) {
      const child = findObjectById(obj.children, id);
      if (child) { return child; }
    }
  }
  return null;
}

export function forEachChildObject(objectList, fn) {
  (objectList || []).forEach((obj) => {
    if (!obj) { return; }
    if (obj.type === 'group' && Array.isArray(obj.children)) {
      forEachChildObject(obj.children, fn);
    } else {
      fn(obj);
    }
  });
}
export function forEachCanvasObject(objectList, fn) {
  (objectList || []).forEach((obj) => {
    if (!obj) { return; }
    fn(obj);
    if (obj.type === 'group' && Array.isArray(obj.children)) {
      forEachCanvasObject(obj.children, fn);
    }
  });
}

export function findParentGroup(objectList, id) {
  for (const obj of objectList) {
    if (obj.type === 'group' && obj.children) {
      if (obj.children.some(c => c.Id === id)) { return obj; }
      const parent = findParentGroup(obj.children, id);
      if (parent) { return parent; }
    }
  }
  return null;
}


export function resolveTopLevelSelection(objectList, items) {
  const topLevelById = new Map();
  (items || []).forEach((item) => {
    if (!item || !item.Id) { return; }
    const activeObject = findObjectById(objectList, item.Id) || item;
    if (activeObject.type === 'group') {
      topLevelById.set(activeObject.Id, activeObject);
      return;
    }
    const parent = findParentGroup(objectList, activeObject.Id);
    if (parent) {
      topLevelById.set(parent.Id, parent);
      return;
    }
    topLevelById.set(activeObject.Id, activeObject);
  });
  return Array.from(topLevelById.values());
}

export function resolveLiveObjects(objectList, items) {
  if (!objectList || !items || !items.length) { return []; }
  const seen = new Set();
  const result = [];
  items.forEach((item) => {
    if (!item) { return; }
    const id   = item.Id || item;
    const activeObject = findObjectById(objectList, id);
    if (!activeObject || seen.has(activeObject.Id)) { return; }
    seen.add(activeObject.Id);
    result.push(activeObject);
  });
  return result;
}

export function getPropertyApplyTargets(objectList, selectedObjectsList, activeObject, selectedTopLevel = null) {
  const selectedGroupChildren = [];
  (objectList || []).forEach((obj) => {
    if (obj && obj.type === 'group' && Array.isArray(obj.children)) {
      obj.children.forEach((child) => {
        if (child && child.isSelected) {
          selectedGroupChildren.push(child);
        }
      });
    }
  });
  if (selectedGroupChildren.length) {
    return resolveLiveObjects(objectList, selectedGroupChildren);
  }
  if (activeObject && activeObject.type !== 'group' && findParentGroup(objectList, activeObject.Id)) {
    const activeObject = findObjectById(objectList, activeObject.Id) || activeObject;
    return activeObject.type === 'group' ? [] : [activeObject];
  }

  const topLevel = (selectedTopLevel && selectedTopLevel.length)
      ? selectedTopLevel
      : (objectList || []).filter(obj => obj && obj.isSelected && obj.type !== 'pagedetails');
  const expandedTopLevel  = resolveLiveObjects(objectList, expandSelectionForProperties(topLevel));
  const fromSelectionList = resolveLiveObjects(objectList, selectedObjectsList);
  const selectedById      = new Map();
  expandedTopLevel.concat(fromSelectionList).forEach((object) => {
    selectedById.set(object.Id, object);
  });
  if (selectedById.size) { return Array.from(selectedById.values()); }

  if (activeObject && activeObject.type === 'group' && Array.isArray(activeObject.children)) {
    return resolveLiveObjects(objectList, expandSelectionForProperties([activeObject]));
  }
  if (activeObject) {
    const activeObject = findObjectById(objectList, activeObject.Id) || activeObject;
    if (activeObject.type === 'group') { return []; }
    return [activeObject];
  }
  return [];
}

export function isSvgStrokeObject(object) {
  return !!(object && object.type === 'image' && object.ImageType
      && (object.ImageType === 'SVGImage'
          || object.ImageType.indexOf('FDICText') !== -1
          || object.ImageType.indexOf('FDICLogo') !== -1));
}

export function isPhotoAnimationImage(object) {
  return !!(object && object.type === 'image'
      && object.ImageType !== 'SVGImage'
      && object.ImageType !== 'FDICLogo'
      && object.ImageType !== 'WhiteboxLogo'
      && object.ImageType !== 'FDICText');
}

export function getAnimationKind(object) {
  if (!object || object.type === 'group' || object.type === 'pagedetails') {
    return null;
  }
  if (object.type === 'image') {
    return isPhotoAnimationImage(object) ? 'photo' : 'svg';
  }
  return object.type;
}

export function objectSupportsAnimationStyle(object, style) {
  const kind = getAnimationKind(object);
  if (!kind) {
    return false;
  }
  const animationStyle = String(style || '');
  if (!animationStyle) {
    return true;
  }
  if (animationStyle === 'typewriter' || animationStyle === 'burst') {
    return kind === 'text';
  }
  if (animationStyle.indexOf('photoZom') !== -1
      || animationStyle.indexOf('photoRise') !== -1
      || animationStyle.indexOf('photoFlow') !== -1) {
    return kind === 'photo';
  }
  return true;
}

export function filterSameTypeAnimationTargets(objects, primaryObject, style) {
  const list = (objects || []).filter(obj => obj && getAnimationKind(obj));
  const primary = (primaryObject && getAnimationKind(primaryObject))
      ? primaryObject
      : list[0];
  if (!primary) {
    return [];
  }

  if (list.length > 1) {
    const firstType = list[0].type;
    const allSameType = list.every(obj => obj.type === firstType);
    if (!allSameType) {
      return objectSupportsAnimationStyle(primary, style) ? [primary] : [];
    }
  }

  return list.filter(obj => objectSupportsAnimationStyle(obj, style));
}

export function applyAbsoluteBoundsToObject(objectList, object, bounds) {
  if (!object || !bounds) { return; }

  const parentGroup = findParentGroup(objectList, object.Id);
  object.Width  = bounds.width;
  object.Height = bounds.height;
  object.X      = bounds.x;
  object.Y      = bounds.y;

  if (parentGroup) {
    recalculateGroupBounds(parentGroup);
  }
}

export function findFirstSelectedObject(objectList) {
  for (const obj of objectList) {
    if (obj.isSelected) { return obj; }
    if (obj.type === 'group' && obj.children) {
      const child = obj.children.find(c => c.isSelected);
      if (child) { return child; }
    }
  }
  return null;
}

export function clearAllSelection(objectList) {
  objectList.forEach((object) => {
    object.isSelected = false;
    if (object.type === 'group' && object.children) {
      clearAllSelection(object.children);
    }
  });
}
export function getNextTableName(objectList) {
  let max = 0;
  forEachChildObject(objectList, (obj) => {
    if (obj.type !== 'table') return;
    const match = /^Table (\d+)$/.exec(obj.Name || '');
    if (match) max = Math.max(max, Number(match[1]));
  });
  return `Table ${max + 1}`;
}
export function assignPdfSortOrderTree(list) {
  const n = list.length;
  list.forEach((item, index) => {
    item.PdfSortOrder = n - 1 - index;
    item.elementIndex = index;
    if (item.type === 'group' && Array.isArray(item.children)) {
      assignPdfSortOrderWithinGroup(item.children);
    }
  });
}

export function assignPdfSortOrderWithinGroup(children) {
  const n = children.length;
  children.forEach((child, index) => {
    child.PdfSortOrder = n - 1 - index;
    child.elementIndex = -1;
    if (child.type === 'group' && Array.isArray(child.children)) {
      assignPdfSortOrderWithinGroup(child.children);
    }
  });
}
export function moveSelectedInStack(list, direction) {
  if (direction === 'front' || direction === 'back') {
    const selected = list.filter(item => item.isSelected);
    const others   = list.filter(item => !item.isSelected);
    list.splice(0, list.length, ...(direction === 'front' ? [...selected, ...others] : [...others, ...selected]));
  } else {
    const step    = direction === 'forward' ? -1 : 1;
    const indexes = list.map((item, index) => index);
    // Walk from the side we move towards, so a selected block moves together.
    if (step === 1) { indexes.reverse(); }
    indexes.forEach((i) => {
      const neighbour = list[i + step];
      if (list[i].isSelected && neighbour && !neighbour.isSelected) {
        const first = Math.min(i, i + step);
        list.splice(first, 2, list[first + 1], list[first]);
      }
    });
  }
  list.forEach((item) => {
    if (item.type === 'group' && Array.isArray(item.children)) {
      moveSelectedInStack(item.children, direction);
    }
  });
}
